import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, jsonb, serial } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// Contact request status enum
export const contactStatusEnum = z.enum(["new", "read", "in_progress", "resolved"]);
export type ContactStatus = z.infer<typeof contactStatusEnum>;

export const contactRequests = pgTable("contact_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  service: text("service").notNull(),
  message: text("message").notNull(),
  preferredDate: text("preferred_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Status management fields
  status: text("status").default("new").notNull(),
  notes: text("notes"),
  readAt: timestamp("read_at"),
  resolvedAt: timestamp("resolved_at"),
});

export const insertContactRequestSchema = createInsertSchema(contactRequests).omit({
  id: true,
  createdAt: true,
}).extend({
  service: z.enum([
    "Bathroom Renovation",
    "Kitchen Remodeling",
    "Whole-Home Renovation",
    "Handyman / Emergency Repair",
    "Custom Woodwork",
    "Property Maintenance Plan"
  ]),
});

export type InsertContactRequest = z.infer<typeof insertContactRequestSchema>;
export type ContactRequest = typeof contactRequests.$inferSelect;

// Schema for updating contact request status/notes
export const updateContactRequestSchema = z.object({
  status: contactStatusEnum.optional(),
  notes: z.string().max(2000).optional(),
});

export type UpdateContactRequest = z.infer<typeof updateContactRequestSchema>;

// =====================
// System Logs (Structured Logging)
// =====================

export const systemLogs = pgTable("system_logs", {
  id: serial("id").primaryKey(),
  level: varchar("level", { length: 10 }).notNull(),
  timestamp: timestamp("timestamp").defaultNow().notNull(),
  details: jsonb("details").notNull(), // Stores the full structured log object
});

// Log categories for security/audit events
export const logCategoryEnum = z.enum([
  "security",
  "audit",
  "request",
  "error",
  "frontend",
]);

// Log event types for querying
export const logEventEnum = z.enum([
  // Security events
  "auth_success",
  "auth_failed",
  "auth_logout",
  "rate_limit_exceeded",
  // Audit events
  "admin_action",
  "contact_request",
  // Error events
  "api_error",
  "frontend_error",
  // Request events
  "api_request",
]);

export const insertSystemLogSchema = createInsertSchema(systemLogs).omit({
  id: true,
}).extend({
  details: z.object({
    event: logEventEnum,
    category: logCategoryEnum,
    message: z.string().optional(),
    // Request context
    method: z.string().optional(),
    url: z.string().optional(),
    ip: z.string().optional(),
    userId: z.string().optional(),
    userAgent: z.string().optional(),
    duration: z.number().optional(),
    statusCode: z.number().optional(),
    // Security context
    email: z.string().optional(),
    reason: z.string().optional(),
    // Admin audit
    action: z.string().optional(),
    targetId: z.string().optional(),
    // Additional context (flexible)
    context: z.record(z.unknown()).optional(),
  }),
});

export type InsertSystemLog = z.infer<typeof insertSystemLogSchema>;
export type SystemLog = typeof systemLogs.$inferSelect;

// =====================
// Media (Cloudinary CMS)
// =====================

// Media type enum for categorization
export const mediaTypeEnum = z.enum(["image", "video"]);
export type MediaType = z.infer<typeof mediaTypeEnum>;

// Media category for organization
export const mediaCategoryEnum = z.enum([
  "portfolio",
  "hero",
  "testimonial",
  "team",
  "service",
  "blog",
  "gallery",
  "other"
]);
export type MediaCategory = z.infer<typeof mediaCategoryEnum>;

export const media = pgTable("media", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Cloudinary fields
  publicId: varchar("public_id", { length: 255 }).notNull().unique(),
  url: varchar("url", { length: 500 }).notNull(),
  secureUrl: varchar("secure_url", { length: 500 }).notNull(),
  resourceType: varchar("resource_type", { length: 50 }).notNull().default("image"),
  format: varchar("format", { length: 20 }),
  width: serial("width"),
  height: serial("height"),
  bytes: serial("bytes"),
  duration: serial("duration"), // For videos (in seconds)
  // Metadata fields
  title: varchar("title", { length: 255 }),
  description: text("description"),
  altText: varchar("alt_text", { length: 500 }),
  category: varchar("category", { length: 50 }).default("other"),
  tags: text("tags"), // Comma-separated tags
  // Display settings
  featured: text("featured").default("false"), // Boolean as text for compatibility
  sortOrder: serial("sort_order"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema for creating media
export const insertMediaSchema = createInsertSchema(media).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  resourceType: mediaTypeEnum.optional(),
  category: mediaCategoryEnum.optional(),
});

// Update schema for editing media
// Uses .nullable().optional() to accept null, undefined, or valid values
export const updateMediaSchema = z.object({
  title: z.string().max(255).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  altText: z.string().max(500).nullable().optional(),
  category: mediaCategoryEnum.nullable().optional(),
  tags: z.string().max(500).nullable().optional(),
  featured: z.string().nullable().optional(),
  sortOrder: z.number().nullable().optional(),
  // For file replacement scenarios (optional - only sent when replacing the media file)
  publicId: z.string().nullable().optional(),
  resourceType: mediaTypeEnum.nullable().optional(),
});

export type InsertMedia = z.infer<typeof insertMediaSchema>;
export type UpdateMedia = z.infer<typeof updateMediaSchema>;
export type Media = typeof media.$inferSelect;
